/**
 * Tagesauswertung ueber alle Gewohnheiten (Roadmap F-08) - eigene Spalte
 * neben den Gewohnheiten: Quote, Erledigt/Offen zum direkten Abhaken, ob der
 * Tag geschafft ist, dazu Gewohnheiten pro Woche, Serie perfekter Tage und der Verlauf.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Flame, Repeat } from 'lucide-react';

import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaColor, useAreaMap } from '@/modules/areas/useAreas';
import { HEATMAP_DAYS, type HabitWithStats } from '@/modules/habits/useHabits';
import { toggleHabitDone } from '@/db/repositories/habits';
import {
  perfekteTageSerie, tagesAuswertung, tagesVerlauf, wochenStand, type TagesPunkt,
} from '@/domain/habits/daily';
import { formatDe } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { Habit } from '@/domain/types';

type Zeitraum = 7 | 30;

export function DayOverview({ eintraege, heute, gefiltert, kompakt = false }: {
  eintraege: HabitWithStats[];
  heute: string;
  /** Bereichsfilter aktiv - dann gilt die Auswertung nur fuer diese Bereiche. */
  gefiltert: boolean;
  /**
   * Fuer die Uebersicht: nur Anschauen und Abhaken (V-02) - kein Umschalter,
   * fest 7 Tage, Erledigtes nur als Zahl, Link zur Gewohnheiten-Seite.
   */
  kompakt?: boolean;
}) {
  const [zeitraum, setZeitraum] = useState<Zeitraum>(7);
  const aktiv = eintraege.filter((e) => !e.habit.archived);
  const tag = tagesAuswertung(aktiv, heute);
  const serie = perfekteTageSerie(aktiv, heute, HEATMAP_DAYS);
  const wochenziele = aktiv.filter((e) => e.habit.cadence.type === 'x_pro_woche');
  const erledigtHeute = new Set(aktiv.filter((e) => e.done.has(heute)).map((e) => e.habit.id));
  const prozent = tag.quote === null ? null : Math.round(tag.quote * 100);
  const summe = tag.erledigt.length + tag.offen.length;

  return (
    <section aria-label={kompakt ? 'Gewohnheiten heute' : 'Heute'} className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <header className="flex items-baseline gap-2">
        {kompakt && <Repeat className="size-4 shrink-0 self-center text-ink-faint" aria-hidden="true" />}
        <h2 className="flex-1 text-sm font-semibold">{kompakt ? 'Gewohnheiten heute' : 'Heute'}</h2>
        {gefiltert && <span className="text-[11px] text-ink-faint">nur gefilterte Bereiche</span>}
        {kompakt && (
          <Link to="/gewohnheiten" className="text-[12px] text-ink-faint underline-offset-2 transition-colors hover:text-ink hover:underline">
            Öffnen
          </Link>
        )}
      </header>

      {prozent === null ? (
        <p className="text-sm text-ink-faint">Heute ist keine Gewohnheit fest vorgesehen.</p>
      ) : (
        <>
          <div className="flex items-end gap-3">
            <span className="text-3xl font-semibold tabular-nums">{prozent} %</span>
            <span className="pb-1 text-sm text-ink-muted">{tag.erledigt.length} von {summe} geschafft</span>
          </div>
          <div
            role="progressbar"
            aria-label="Tagesquote"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={prozent}
            className="h-1.5 w-full overflow-hidden rounded-full bg-line"
          >
            <div
              className={cn('h-full rounded-full transition-[width]', tag.alleGeschafft ? 'bg-ok' : 'bg-accent')}
              style={{ width: `${prozent}%` }}
            />
          </div>
          <p className={cn('flex items-center gap-1.5 text-sm', tag.alleGeschafft ? 'text-ok' : 'text-ink-muted')}>
            {tag.alleGeschafft && <CheckCircle2 className="size-4" aria-hidden="true" />}
            Alles geschafft: <strong className="font-semibold">{tag.alleGeschafft ? 'Ja' : 'Nein'}</strong>
          </p>

          <Liste titel={`Offen (${tag.offen.length})`} habits={tag.offen} erledigt={false} heute={heute} />
          {kompakt
            ? tag.erledigt.length > 0 && <p className="text-[12px] text-ink-faint">{tag.erledigt.length} erledigt</p>
            : <Liste titel={`Erledigt (${tag.erledigt.length})`} habits={tag.erledigt} erledigt heute={heute} />}
        </>
      )}

      {wochenziele.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-line pt-2.5">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">Pro Woche</h3>
          <p className="text-[11px] text-ink-faint">Ohne festen Tag - zählen nicht zur Tagesquote.</p>
          <ul className="flex flex-col gap-1">
            {wochenziele.map(({ habit, done }) => {
              const soll = habit.cadence.type === 'x_pro_woche' ? habit.cadence.count : 0;
              const stand = wochenStand(done, heute);
              return (
                <Zeile key={habit.id} habit={habit} erledigt={erledigtHeute.has(habit.id)} heute={heute}>
                  <span className={cn('shrink-0 text-[11px] tabular-nums', stand >= soll ? 'text-ok' : 'text-ink-faint')}>
                    {stand}/{soll} diese Woche
                  </span>
                </Zeile>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-line pt-2.5">
        <p className="flex items-center gap-1.5 text-sm text-ink-muted">
          <Flame className="size-4 text-accent-text" aria-hidden="true" />
          Perfekte Tage in Folge:{' '}
          <strong className="font-semibold tabular-nums text-ink">{serie.tage}{serie.mindestens ? '+' : ''}</strong>
        </p>
        <div className="flex items-center gap-1.5 text-[12px]">
          <span className="flex-1 text-ink-faint">Tagesquote im Verlauf{kompakt ? ' (7 Tage)' : ''}</span>
          {!kompakt && ([7, 30] as Zeitraum[]).map((z) => (
            <button
              key={z}
              type="button"
              aria-pressed={zeitraum === z}
              onClick={() => setZeitraum(z)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 transition-colors',
                zeitraum === z ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:text-ink',
              )}
            >
              {z} Tage
            </button>
          ))}
        </div>
        <Verlauf punkte={tagesVerlauf(aktiv, heute, kompakt ? 7 : zeitraum)} />
      </div>
    </section>
  );
}

function Liste({ titel, habits, erledigt, heute }: { titel: string; habits: Habit[]; erledigt: boolean; heute: string }) {
  if (habits.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">{titel}</h3>
      <ul className="flex flex-col gap-1">
        {habits.map((h) => <Zeile key={h.id} habit={h} erledigt={erledigt} heute={heute} />)}
      </ul>
    </div>
  );
}

function Zeile({ habit, erledigt, heute, children }: {
  habit: Habit; erledigt: boolean; heute: string; children?: React.ReactNode;
}) {
  const areaColor = useAreaColor();
  const bereich = useAreaMap().get(habit.areaId ?? '')?.name;
  return (
    <li className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={erledigt}
        onChange={() => void toggleHabitDone(habit.id, heute)}
        aria-label={`${habit.name} heute erledigt`}
        className="size-4 shrink-0 accent-[var(--color-accent)]"
      />
      <AreaDot color={areaColor(habit.areaId)} size="sm" name={bereich} />
      <span className={cn('min-w-0 flex-1 truncate', erledigt && 'text-ink-faint line-through')}>{habit.name}</span>
      {children}
    </li>
  );
}

/** Kleines Balkendiagramm: 100 % gruen, sonst Akzent, "nichts vorgesehen" als flacher Strich. */
function Verlauf({ punkte }: { punkte: TagesPunkt[] }) {
  return (
    <div>
      <ol aria-label="Tagesquote je Tag" className="flex h-16 items-end gap-px">
        {punkte.map((p) => {
          const text = p.quote === null
            ? `${formatDe(p.date, 'EEEEEE dd.MM.')}: nichts vorgesehen`
            : `${formatDe(p.date, 'EEEEEE dd.MM.')}: ${p.erledigt}/${p.vorgesehen} (${Math.round(p.quote * 100)} %)`;
          return (
            <li key={p.date} title={text} aria-label={text} className="flex h-full min-w-0 flex-1 items-end">
              <span
                className={cn(
                  'block w-full rounded-sm',
                  p.quote === null ? 'bg-surface-raised' : p.quote === 1 ? 'bg-ok' : 'bg-accent',
                )}
                style={{ height: p.quote === null ? '2px' : `${Math.max(4, p.quote * 100)}%` }}
              />
            </li>
          );
        })}
      </ol>
      <div className="mt-1 flex justify-between text-[10px] tabular-nums text-ink-faint">
        <span>{formatDe(punkte[0]!.date, 'dd.MM.')}</span>
        <span>heute</span>
      </div>
    </div>
  );
}
